import { useState, useEffect, useRef } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import video from '../assets/rosecity.mp4';

// Global styles for smooth scrolling and overall theme
const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: 'Inter', 'Segoe UI', sans-serif;
    overflow-x: hidden;
    color: #f8fafc;
    background: #0f0f1a;
  }
  
  html {
    scroll-behavior: smooth;
  }
`;

// Particle background component
const ParticlesBackground = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const mousePosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = window.innerWidth;
    const height = window.innerHeight;

    canvas.width = width;
    canvas.height = height;

    // Create particles
    const createParticles = () => {
      particles.current = Array.from({ length: 50 }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 3 + 1,
        speedX: Math.random() * 0.5 - 0.25,
        speedY: Math.random() * 0.5 - 0.25,
        color: `hsl(${Math.random() * 60 + 280}, 70%, 60%)`,
      }));
    };

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      
      particles.current.forEach((particle) => {
        // Update position
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        // Wrap around edges
        if (particle.x > width) particle.x = 0;
        if (particle.x < 0) particle.x = width;
        if (particle.y > height) particle.y = 0;
        if (particle.y < 0) particle.y = height;
        
        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();
      });
      
      requestAnimationFrame(animate);
    };

    createParticles();
    animate();

    // Handle mouse movement for potential interactivity
    const handleMouseMove = (e) => {
      mousePosition.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      createParticles();
    });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return <ParticlesCanvas ref={canvasRef} />;
};

const ParticlesCanvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -2;
  opacity: 0.5;
`;

// Keyframe animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled components
const VideoBackground = styled(motion.video)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  object-fit: cover;
  z-index: -1;
  filter: brightness(0.7) contrast(1.1);
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, rgba(91, 33, 182, 0.4), rgba(144, 58, 255, 0.4), rgba(199, 0, 123, 0.4));
    background-size: 200% 200%;
    animation: ${gradientShift} 15s ease infinite;
    mix-blend-mode: overlay;
  }
`;

const HeroSection = styled(motion.div)`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled(motion.div)`
  max-width: 900px;
  padding: 2rem;
  z-index: 2;

  h1 {
    font-size: 4rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    background: linear-gradient(45deg, #ff4d8d, #6c28d9, #00c6fb);
    background-size: 200% 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 5s ease infinite, ${fadeIn} 1.5s ease-out;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  p {
    font-size: 1.35rem;
    line-height: 1.6;
    margin-bottom: 2rem;
    animation: ${fadeIn} 1.8s ease-out;
    color: #e2e8f0;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }

  a {
    display: inline-block;
    margin-top: 2rem;
    padding: 1rem 2.5rem;
    background: linear-gradient(45deg, #6c28d9, #ff4d8d);
    color: white;
    border-radius: 50px;
    text-decoration: none;
    font-weight: 600;
    letter-spacing: 0.5px;
    transition: all 0.4s ease;
    box-shadow: 0 10px 20px rgba(108, 40, 217, 0.3);
    animation: ${pulse} 2.5s infinite ease-in-out, ${fadeIn} 2s ease-out;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 25px rgba(108, 40, 217, 0.4);
      background: linear-gradient(45deg, #ff4d8d, #6c28d9);
    }
  }
`;

const ShadowSection = styled(motion.div)`
  background: linear-gradient(180deg, #0f0f1a 0%, #1a1034 100%);
  padding: 6rem 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -100px;
    left: 0;
    width: 100%;
    height: 100px;
    background: linear-gradient(180deg, transparent, #1a1034);
    z-index: 1;
  }
`;

const Content = styled(motion.div)`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  h2 {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 2rem;
    background: linear-gradient(45deg, #ff4d8d, #6c28d9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.25rem;
    line-height: 1.8;
    color: #cbd5e1;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 1;
`;

const FloatingElement = styled.div`
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(108, 40, 217, 0.2), rgba(255, 77, 141, 0.2));
  filter: blur(4px);
  animation: ${float} 8s infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
  
  &:nth-child(1) {
    width: 100px;
    height: 100px;
    top: 20%;
    left: 10%;
  }
  
  &:nth-child(2) {
    width: 150px;
    height: 150px;
    top: 60%;
    right: 15%;
  }
  
  &:nth-child(3) {
    width: 70px;
    height: 70px;
    bottom: 20%;
    left: 20%;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #cbd5e1;
  font-size: 0.9rem;
  z-index: 5;
`;

const Chevron = styled(motion.div)`
  width: 20px;
  height: 20px;
  border-right: 2px solid #cbd5e1;
  border-bottom: 2px solid #cbd5e1;
  transform: rotate(45deg);
  margin-top: 8px;
`;

function Home() {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const scale = useTransform(scrollY, [0, 300], [1, 0.95]);
  
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const cursorX = useTransform(scrollY, [0, 300], [mousePosition.x * 0.01, 0]);
  const cursorY = useTransform(scrollY, [0, 300], [mousePosition.y * 0.01, 0]);

  return (
    <>
      <GlobalStyle />
      <ParticlesBackground />
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <VideoBackground
          autoPlay
          loop
          muted
          style={{ 
            scale,
            x: cursorX,
            y: cursorY
          }}
        >
          <source src={video} type="video/mp4" />
        </VideoBackground>
        
        <HeroSection>
          <FloatingElements>
            <FloatingElement delay="0s" />
            <FloatingElement delay="2s" />
            <FloatingElement delay="4s" />
          </FloatingElements>
          
          <HeroContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2 }}
          >
            <h1>Welcome to RoseCity Web3 <span className="rose-icon">🌹</span> </h1>
            <p>
              RCW3 is an art collective based in Portland, Oregon, serving all of Web3! We empower IRL artists, creators and entrepreneurs to tap into the power of blockchain technology for the betterment of themselves and their communities!
            </p>
            <motion.a 
              href="/about"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Learn More
            </motion.a>
          </HeroContent>
          
          <ScrollIndicator
            style={{ opacity }}
            initial={{ y: -10 }}
            animate={{ y: 10 }}
            transition={{ 
              repeat: Infinity,
              repeatType: "reverse",
              duration: 1.5
            }}
          >
            Scroll to explore
            <Chevron
              initial={{ y: 0 }}
              animate={{ y: 5 }}
              transition={{ 
                repeat: Infinity,
                repeatType: "reverse",
                duration: 1.5
              }}
            />
          </ScrollIndicator>
        </HeroSection>
        
        <ShadowSection
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
          viewport={{ once: true }}
        >
          <Content
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h2>Our Mission</h2>
            <p>
              Our mission is to empower artists, creators, and entrepreneurs by providing web3 tools and platforms for innovation, collaboration, and brand/artist growth. Through our events, we create community by providing education and onboarding opportunities into this vibrant digital revolution!
            </p>
          </Content>
        </ShadowSection>
      </div>
    </>
  );
}

export default Home;
